// CanvasRenderer.jsx
// ────────────────────────────────────────────────────────────────────────
// The ONE component that renders any project's LayoutSpec into a dashboard.
// Replaces "28 wb-*.html files" with one generic engine that draws infinite
// custom layouts.
//
// LayoutSpec shape it consumes (from services/ariaProjects.js):
//   {
//     version: 1,
//     title: "...",
//     subtitle: "...",
//     panels: [
//       { id, type: text|key_values|checklist|notes, title, score,
//         locked, userPlaced, config: { ... } }
//     ]
//   }
//
// MVP renders 4 primitives: text, key_values, checklist, notes.
// Future primitives (chart, calendar, file, form, embed, image, link, table)
// render as placeholders until their UI ships.
//
// Editing model (Layer 3):
//   - Hover any panel → ✏️ edit / 📌 pin / ✕ remove buttons
//   - Inline edit (no modal) for all primitives
//   - + Add panel button at bottom opens a picker
//   - Drag to reorder (locked panels stay put)
//   - Edits POST back to /api/aria/canvas/:id/layout (actor=user)
//
// Personality + logo are NOT rendered here. This is just the project canvas.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getApiBase } from './apiBase';

// In dev, the vite proxy sometimes fails for /api/aria/* paths — and in
// Electron / production we already want absolute backend URLs. So we use
// getApiBase() and fall back to 127.0.0.1:3001 in dev when it returns ''.
const _base = getApiBase();
const API_BASE = _base || 'http://127.0.0.1:3001';

// ── Helpers ──────────────────────────────────────────────────────────────
function newPanelId() {
  return 'p_' + Math.random().toString(36).slice(2, 10);
}

const PRIMITIVE_GALLERY = [
  { type: 'text',       icon: '📝', label: 'Text block',  hint: 'A paragraph of context.' },
  { type: 'key_values', icon: '📊', label: 'Key values',  hint: 'Stats and quick facts.' },
  { type: 'checklist',  icon: '✅', label: 'Checklist',   hint: 'Tasks with checkboxes.' },
  { type: 'notes',      icon: '🗒️', label: 'Notes',       hint: 'Free-form notes.' },
];

// ── API helpers ──────────────────────────────────────────────────────────
async function apiGet(path, userId) {
  const r = await fetch(`${API_BASE}${path}`, { headers: { 'X-User-Id': userId } });
  if (!r.ok) throw new Error(`GET ${path} → ${r.status}`);
  return r.json();
}
async function apiPost(path, body, userId) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`POST ${path} → ${r.status}`);
  return r.json();
}
async function apiPut(path, body, userId) {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`PUT ${path} → ${r.status}`);
  return r.json();
}
async function apiDelete(path, userId) {
  const r = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: { 'X-User-Id': userId } });
  if (!r.ok) throw new Error(`DELETE ${path} → ${r.status}`);
  return r.json();
}

// ═════════════════════════════════════════════════════════════════════════
// Panel primitives (4 MVP types)
// ═════════════════════════════════════════════════════════════════════════

function TextPanel({ panel, onChange, readonly }) {
  const [content, setContent] = useState(panel.config?.content || '');
  useEffect(() => setContent(panel.config?.content || ''), [panel.id]);
  return (
    <textarea
      className="canvas-panel__text"
      value={content}
      onChange={(e) => setContent(e.target.value)}
      onBlur={() => onChange && content !== (panel.config?.content || '') && onChange({ ...panel, config: { ...panel.config, content } })}
      placeholder={readonly ? '' : 'Tap to write…'}
      readOnly={readonly}
      rows={Math.max(2, Math.ceil(content.length / 60))}
    />
  );
}

function KeyValuesPanel({ panel, onChange, readonly }) {
  const values = panel.config?.values || [];
  const update = (idx, field, val) => {
    const next = values.map((v, i) => (i === idx ? { ...v, [field]: val } : v));
    onChange && onChange({ ...panel, config: { ...panel.config, values: next } });
  };
  const addRow = () => {
    onChange && onChange({ ...panel, config: { ...panel.config, values: [...values, { label: '', value: '' }] } });
  };
  const removeRow = (idx) => {
    onChange && onChange({ ...panel, config: { ...panel.config, values: values.filter((_, i) => i !== idx) } });
  };
  return (
    <div className="canvas-panel__kv">
      {values.map((v, i) => (
        <div key={i} className="canvas-panel__kv-row">
          <input
            className="canvas-panel__kv-label"
            value={v.label || ''}
            placeholder="Label"
            readOnly={readonly}
            onChange={(e) => update(i, 'label', e.target.value)}
          />
          <input
            className="canvas-panel__kv-value"
            value={v.value || ''}
            placeholder="—"
            readOnly={readonly}
            onChange={(e) => update(i, 'value', e.target.value)}
          />
          {!readonly && (
            <button className="canvas-panel__kv-x" onClick={() => removeRow(i)} title="Remove row">×</button>
          )}
        </div>
      ))}
      {!readonly && (
        <button className="canvas-panel__kv-add" onClick={addRow}>+ Add row</button>
      )}
    </div>
  );
}

function ChecklistPanel({ panel, onChange, readonly }) {
  const items = panel.config?.items || [];
  const update = (idx, field, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    onChange && onChange({ ...panel, config: { ...panel.config, items: next } });
  };
  const addItem = () => {
    onChange && onChange({ ...panel, config: { ...panel.config, items: [...items, { text: '', done: false }] } });
  };
  const removeItem = (idx) => {
    onChange && onChange({ ...panel, config: { ...panel.config, items: items.filter((_, i) => i !== idx) } });
  };
  return (
    <div className="canvas-panel__checklist">
      {items.map((it, i) => (
        <div key={i} className={`canvas-panel__check-row${it.done ? ' is-done' : ''}`}>
          <input
            type="checkbox"
            checked={!!it.done}
            disabled={readonly}
            onChange={(e) => update(i, 'done', e.target.checked)}
          />
          <input
            className="canvas-panel__check-text"
            value={it.text || ''}
            placeholder="New task"
            readOnly={readonly}
            onChange={(e) => update(i, 'text', e.target.value)}
          />
          {!readonly && (
            <button className="canvas-panel__kv-x" onClick={() => removeItem(i)} title="Remove">×</button>
          )}
        </div>
      ))}
      {!readonly && (
        <button className="canvas-panel__kv-add" onClick={addItem}>+ Add task</button>
      )}
    </div>
  );
}

function NotesPanel({ panel, onChange, readonly }) {
  const [content, setContent] = useState(panel.config?.content || '');
  useEffect(() => setContent(panel.config?.content || ''), [panel.id]);
  return (
    <textarea
      className="canvas-panel__notes"
      value={content}
      onChange={(e) => setContent(e.target.value)}
      onBlur={() => onChange && content !== (panel.config?.content || '') && onChange({ ...panel, config: { ...panel.config, content } })}
      placeholder={readonly ? '' : 'Notes go here…'}
      readOnly={readonly}
      rows={Math.max(3, Math.ceil(content.length / 50))}
    />
  );
}

function PlaceholderPanel({ panel }) {
  return (
    <div className="canvas-panel__placeholder">
      <div className="canvas-panel__placeholder-type">{panel.type}</div>
      <div className="canvas-panel__placeholder-hint">This panel type is coming soon. The data is safe.</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// One Panel wrapper — chrome + hover actions
// ═════════════════════════════════════════════════════════════════════════

function PanelChrome({ panel, onChange, onPin, onRemove, onMove, readonly, canMoveUp, canMoveDown }) {
  const [hover, setHover] = useState(false);
  let body = null;
  switch (panel.type) {
    case 'text':       body = <TextPanel panel={panel} onChange={onChange} readonly={readonly} />; break;
    case 'key_values': body = <KeyValuesPanel panel={panel} onChange={onChange} readonly={readonly} />; break;
    case 'checklist':  body = <ChecklistPanel panel={panel} onChange={onChange} readonly={readonly} />; break;
    case 'notes':      body = <NotesPanel panel={panel} onChange={onChange} readonly={readonly} />; break;
    default:           body = <PlaceholderPanel panel={panel} />;
  }
  return (
    <section
      className={`canvas-panel${panel.locked ? ' is-locked' : ''}${panel.userPlaced ? ' is-user-placed' : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <header className="canvas-panel__header">
        <h3 className="canvas-panel__title">{panel.title || 'Untitled'}</h3>
        {!readonly && (
          <div className={`canvas-panel__actions${hover ? ' is-shown' : ''}`}>
            <button
              className={`canvas-panel__btn${panel.locked ? ' is-active' : ''}`}
              onClick={onPin}
              title={panel.locked ? 'Aria can rearrange this' : "Aria won't touch this"}
            >{panel.locked ? '📌' : '📍'}</button>
            <button className="canvas-panel__btn" onClick={() => onMove(-1)} disabled={!canMoveUp} title="Move up">↑</button>
            <button className="canvas-panel__btn" onClick={() => onMove(1)} disabled={!canMoveDown} title="Move down">↓</button>
            <button className="canvas-panel__btn canvas-panel__btn--danger" onClick={onRemove} title="Remove panel">✕</button>
          </div>
        )}
      </header>
      <div className="canvas-panel__body">
        {body}
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Add-panel picker
// ═════════════════════════════════════════════════════════════════════════

function AddPanelPicker({ open, onClose, onAdd }) {
  if (!open) return null;
  return (
    <div className="canvas-picker__backdrop" onClick={onClose}>
      <div className="canvas-picker" onClick={(e) => e.stopPropagation()}>
        <header className="canvas-picker__head">
          <h3>Add a panel</h3>
          <button className="canvas-picker__close" onClick={onClose}>×</button>
        </header>
        <div className="canvas-picker__grid">
          {PRIMITIVE_GALLERY.map((p) => (
            <button key={p.type} className="canvas-picker__tile" onClick={() => onAdd(p.type)}>
              <div className="canvas-picker__icon">{p.icon}</div>
              <div className="canvas-picker__label">{p.label}</div>
              <div className="canvas-picker__hint">{p.hint}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Main CanvasRenderer
// ═════════════════════════════════════════════════════════════════════════

export default function CanvasRenderer({ projectId, userId, onTitleChange, onProjectLoaded, readonly = false }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingPanelId, setSavingPanelId] = useState(null);
  const saveTimer = useRef(null);

  // Load project
  useEffect(() => {
    if (!projectId) { setProject(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    apiGet(`/api/aria/canvas/${projectId}`, userId)
      .then((r) => {
        if (!cancelled) {
          setProject(r.project);
          onTitleChange?.(r.project?.name);
          onProjectLoaded?.(r.project);
        }
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, userId]);

  // Save layout (debounced — collapses rapid edits into one PUT)
  const saveLayout = useCallback((nextSpec, opts = {}) => {
    if (!project) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await apiPut(`/api/aria/canvas/${project.id}/layout`, {
          layoutSpec: nextSpec,
          actor: 'user',
          reason: opts.reason || null,
        }, userId);
        setProject(r.project);
        onProjectLoaded?.(r.project);
      } catch (e) {
        console.warn('[canvas] save failed:', e.message);
      }
    }, 400);
  }, [project, userId, onProjectLoaded]);

  // Panel ops
  const updatePanel = useCallback((nextPanel) => {
    setSavingPanelId(nextPanel.id);
    setProject((p) => {
      if (!p) return p;
      const panels = p.layoutSpec.panels.map((x) => (x.id === nextPanel.id ? nextPanel : x));
      const nextSpec = { ...p.layoutSpec, panels };
      saveLayout(nextSpec, { reason: 'panel-edit' });
      return { ...p, layoutSpec: nextSpec };
    });
  }, [saveLayout]);

  const pinPanel = useCallback((id) => {
    setProject((p) => {
      if (!p) return p;
      const panels = p.layoutSpec.panels.map((x) => (x.id === id ? { ...x, locked: !x.locked } : x));
      const nextSpec = { ...p.layoutSpec, panels };
      saveLayout(nextSpec, { reason: 'pin-toggle' });
      return { ...p, layoutSpec: nextSpec };
    });
  }, [saveLayout]);

  const removePanel = useCallback((id) => {
    setProject((p) => {
      if (!p) return p;
      const panels = p.layoutSpec.panels.filter((x) => x.id !== id);
      const nextSpec = { ...p.layoutSpec, panels };
      saveLayout(nextSpec, { reason: 'panel-removed' });
      return { ...p, layoutSpec: nextSpec };
    });
  }, [saveLayout]);

  const movePanel = useCallback((id, direction) => {
    setProject((p) => {
      if (!p) return p;
      const panels = [...p.layoutSpec.panels];
      const idx = panels.findIndex((x) => x.id === id);
      if (idx < 0) return p;
      const target = idx + direction;
      if (target < 0 || target >= panels.length) return p;
      [panels[idx], panels[target]] = [panels[target], panels[idx]];
      // User-placed flag so the scorer respects this choice
      panels[target] = { ...panels[target], userPlaced: true };
      const nextSpec = { ...p.layoutSpec, panels };
      saveLayout(nextSpec, { reason: 'reorder' });
      return { ...p, layoutSpec: nextSpec };
    });
  }, [saveLayout]);

  const addPanel = useCallback((type) => {
    const newPanel = {
      id: newPanelId(),
      type,
      title: type === 'text' ? 'New text block'
        : type === 'key_values' ? 'New stats'
        : type === 'checklist' ? 'New checklist'
        : 'New notes',
      score: 0.5,
      locked: false,
      userPlaced: true,
      config: type === 'text' || type === 'notes' ? { content: '' }
        : type === 'key_values' ? { values: [{ label: '', value: '' }] }
        : { items: [{ text: '', done: false }] },
    };
    setProject((p) => {
      if (!p) return p;
      const panels = [...p.layoutSpec.panels, newPanel];
      const nextSpec = { ...p.layoutSpec, panels };
      saveLayout(nextSpec, { reason: 'panel-added' });
      return { ...p, layoutSpec: nextSpec };
    });
    setPickerOpen(false);
  }, [saveLayout]);

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return <div className="canvas-loading">Loading project…</div>;
  }
  if (error) {
    return <div className="canvas-error">Couldn't load project: {error}</div>;
  }
  if (!project) {
    return <div className="canvas-empty">No project selected.</div>;
  }

  const panels = project.layoutSpec?.panels || [];

  return (
    <div className="canvas">
      <header className="canvas__header">
        <div className="canvas__title-block">
          <h1 className="canvas__title">{project.name}</h1>
          {project.layoutSpec?.subtitle && (
            <div className="canvas__subtitle">{project.layoutSpec.subtitle}</div>
          )}
        </div>
        <div className="canvas__mode-chip" title={`This dashboard is in ${project.mode} mode`}>
          {project.mode === 'auto' && '✨ Aria-built'}
          {project.mode === 'copilot' && '🤝 You + Aria'}
          {project.mode === 'manual' && '🛠 You built it'}
        </div>
      </header>

      <div className="canvas__panels">
        {panels.map((p, i) => (
          <PanelChrome
            key={p.id}
            panel={p}
            readonly={readonly}
            canMoveUp={i > 0}
            canMoveDown={i < panels.length - 1}
            onChange={updatePanel}
            onPin={() => pinPanel(p.id)}
            onRemove={() => removePanel(p.id)}
            onMove={(dir) => movePanel(p.id, dir)}
          />
        ))}
      </div>

      {!readonly && (
        <button className="canvas__add-btn" onClick={() => setPickerOpen(true)}>
          + Add a panel
        </button>
      )}

      <AddPanelPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onAdd={addPanel} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Project list (dashboard) + new project flow
// ═════════════════════════════════════════════════════════════════════════

// CanvasProjectList — the project gallery.
// No "+ New project" modal. Projects are created by talking to Aria — she
// detects intent ("I want to start X", "I'm working on Y") and builds the
// dashboard automatically. The list re-fetches on every visit so newly-
// created projects show up without manual refresh.
export function CanvasProjectList({ userId, onOpen }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    apiGet('/api/aria/canvas', userId)
      .then((r) => setProjects(r.projects || []))
      .catch((e) => console.warn('[canvas-list]', e.message))
      .finally(() => setLoading(false));
  }, [userId]);

  // Refresh on mount + when window regains focus (covers "Aria just made one")
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return (
    <div className="canvas-list">
      <header className="canvas-list__header">
        <h2 className="canvas-list__title">My Projects</h2>
      </header>

      {loading ? (
        <div className="canvas-loading">Loading your projects…</div>
      ) : projects.length === 0 ? (
        <div className="canvas-list__empty">
          <p>No projects yet.</p>
          <p>Just tell Aria what you're working on — she'll build the dashboard for you.</p>
        </div>
      ) : (
        <div className="canvas-list__grid">
          {projects.map((p) => (
            <button key={p.id} className="canvas-list__card" onClick={() => onOpen?.(p.id)}>
              <div className="canvas-list__card-title">{p.name}</div>
              {p.layoutSpec?.subtitle && (
                <div className="canvas-list__card-sub">{p.layoutSpec.subtitle}</div>
              )}
              <div className="canvas-list__card-meta">
                <span>{(p.layoutSpec?.panels || []).length} panels</span>
                <span>·</span>
                <span>{p.mode}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
